import { Box } from '@mui/material';
import { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import { JSX, ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getDiffPackageVersion, searchConfiguration } from '../../api/ConfigurationApi';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import Tooltip from '@mui/material/Tooltip';
import {
  checkIsFeatureModified,
  checkIsOptionAdded,
  checkIsOptionRemoved,
  checkIsPropertiesModified,
  checkIsFeatureRemoved,
  getValueById,
  checkIsOptionRemovedFromUpdated,
} from '../../services/DetailsPageHelper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState, AppStore } from '../../store/AppStore';
import {
  addConfiguration,
  getConfigurationData,
  getSalesText,
  updateDiffFeature,
} from '../../store/slice/ConfigurationSlice';
import { EColumns, EDiffAction, EView } from '../../data/Constants';
import {
  IConfigurationDetail,
  IDetailDataTableProps,
  IDetailPageRow,
  IDiffResponse,
  ISalesText,
  ITranslateProps,
} from '../../../types';
import DiffInfDialog from './DIffInfoDialog';
import { TabDrawer } from '../../components/Layouts/TabDrawer';
import { Unauthorized } from '../../components/UnAuthorized';
import {
  createConfigureRequest,
  getPackageDetails,
} from '../../services/ConfigurationDataHelperFunctions';

/**
 * This function returns the feature name using salestext
 * @param {IDetailPageRow} params current row oject
 * @param {Map<string,Map<string,ISalesText>>} salesText sales text data
 * @returns {string} returns feature or option name
 */
function getCellValue(
  params: { row: IDetailPageRow },
  salesText: Map<string, Map<string, ISalesText>>
): string {
  if (params?.row?.isProduct) {
    const productName =
      getValueById(salesText, params?.row?.productId)?.text ?? params?.row?.sectionName;
    return params?.row?.productId ? params?.row?.productId + ' (' + productName + ')' : productName;
  } else if (params?.row?.isSection) {
    return params?.row?.sectionName;
  } else if (params?.row?.featureFamily !== '') {
    const featureName =
      getValueById(salesText, params?.row?.featureFamilyCode)?.text ?? params?.row?.featureFamily;
    return params?.row?.featureFamilyCode + ' (' + featureName + ')';
  }
  return '';
}

/**
 * This function returns the  option name using salestext
 * @param {IDetailPageRow} params current row oject
 * @param {Map<string,Map<string,ISalesText>>} salesText sales text data
 * @returns {string} returns feature or option name
 */
function getOptions(
  params: IDetailPageRow,
  salesText: Map<string, Map<string, ISalesText>>
): string {
  const optionName = getValueById(salesText, params?.featureCode)?.text ?? params?.feature;
  return params?.featureCode ? params?.featureCode + ' (' + optionName + ')' : '';
}

/**
 * This function returs the columns
 * @param {IDiffResponse} diffResponse diffrent package api response
 * @param  {ITranslateProps} t translation key
 * @returns {GridColDef []} returns columns array
 */
function getColumns(
  diffResponse: IDiffResponse,
  handleOpenDiffDialog,
  t: ITranslateProps
): GridColDef[] {
  const salesText = AppStore.getState().configuration.shortSalesText;
  return [
    {
      field: EColumns.FeatureFamilyCode,
      flex: 1,
      headerName: t('label.featureCode'),
      minWidth: 100,
      renderCell: (params) => {
        return getCellValue(params, salesText);
      },
    },
    {
      field: EColumns.FeatureAction,
      flex: 0.25,
      headerName: '',
      minWidth: 10,
      renderCell: (params) => {
        return showFeatureIcons(params, diffResponse, handleOpenDiffDialog, t);
      },
    },
    {
      field: EColumns.OptionCode,
      flex: 1,
      headerName: t('label.optionCode'),
      minWidth: 100,
      valueGetter: (_, params) => {
        return getOptions(params, salesText);
      },
    },
    {
      field: EColumns.OptionAction,
      flex: 0.25,
      headerName: '',
      minWidth: 10,
      renderCell: (params) => {
        return showOptionIcons(params, diffResponse, handleOpenDiffDialog, t);
      },
    },
  ];
}

/**
 * This function returns the class name for the  selected row
 * @param {IDetailPageRow} params
 * @returns {string } returns class name
 */
function getRowClassName(params: { row: IDetailPageRow }): string {
  if (params.row.isProduct) {
    return 'product-row-highlight';
  } else if (params.row.isSection) {
    return 'row-highlight';
  } else {
    return '';
  }
}

/**
 * This function returns the class name for the cell
 * @param {IDetailPageRow} params input params
 * @param {string} searchText search text
 * @returns {string} returns className
 */
function getCellClassName(params: { row: IDetailPageRow }, searchText: string): string {
  if (params.row.added && searchText === '') {
    return 'addedFetureStyle';
  }

  if (params == null || searchText === '') {
    return '';
  }

  if (params?.field === EColumns.FeatureFamilyCode) {
    const featureColumn = params.row.featureFamilyCode + ' (' + params.row.featureFamily + ')';
    return returnRowCSS(featureColumn, searchText, params.row.added);
  }

  if (params?.field === EColumns.OptionCode) {
    const optionColumn = params.row.featureCode + ' (' + params.row.feature + ')';
    return returnRowCSS(optionColumn, searchText, params.row.added);
  }
  return '';
}

/**
 * This function returns the class name for the rows
 * @param {IDetailPageRow} params input params
 * @param {string} searchText search text
 * @param {boolean} rowAdded is row added
 * @returns {string} returns className
 */
function returnRowCSS(text: string, searchText: string, rowAdded: boolean): string {
  if (text?.toLowerCase().includes(searchText.toLowerCase())) {
    return 'highlight';
  } else if (rowAdded) {
    return 'addedFetureStyle';
  }
  return '';
}

/**
 * This component renders the Details DataTable to show the Feature and options
 * @param {IDetailDataTableProps} props input params
 * @returns {ReactNode} returns data table component
 */
function DisplayTableView(props: Readonly<IDetailDataTableProps>): ReactNode {
  const { pmDetails, diffResponse } = props;

  const { t } = useTranslation();

  const [openDiffDialog, setOpenDiffDialog] = useState(false);
  const [featureCode, setFeatureCode] = useState('');

  const searchValue = useSelector((state: AppState) => state.configuration.searchValue);

  const handleOpenDiffDialog = (row: IDetailPageRow) => {
    setOpenDiffDialog(true);
    setFeatureCode(row.featureFamilyCode);
  };

  const handCloseDiffDialog = () => {
    setOpenDiffDialog(false);
  };

  const columns = getColumns(diffResponse, handleOpenDiffDialog, t);

  return (
    <>
      <Box className="detail-data-table">
        <DataGrid
          rows={pmDetails}
          columns={columns}
          rowHeight={26}
          hideFooter={true}
          disableColumnMenu
          disableColumnSorting={true}
          getCellClassName={(params: GridCellParams<number>) => {
            return getCellClassName(params, searchValue);
          }}
          getRowClassName={getRowClassName}
          className="customDataGrid"
        />
      </Box>
      <DiffInfDialog
        open={openDiffDialog}
        diffResponse={diffResponse}
        featureCode={featureCode}
        handCloseDialog={handCloseDiffDialog}
      />
    </>
  );
}

/**
 * This component renders the Details page component
 * @returns {JSX.Element} returns the Details page component
 */
export function DetailsPage(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const configState = useLocation();
  const params = useParams();

  const [diffResponse, setDiffResponse] = useState<IDiffResponse>();

  const configuration = useSelector((state: AppState) => state.configuration);
  const user = useSelector((state: AppState) => state.user);

  async function getDiffPackageDetails() {
    const diffFormData = {
      configurationId: String(configuration.configurationId),
    };

    const response = await getDiffPackageVersion(diffFormData);
    if (response?.data) {
      setDiffResponse(response.data);
    }
  }

  useEffect(() => {
    if ((user.details && configState?.state?.configId) || params.configurationId) {
      if (user.details) {
        getConfigurationDetails();
      }
    } else {
      navigate('/cva');
    }
  }, []);

  useEffect(() => {
    if (configuration?.data) {
      //get salestext
      if (!configuration.shortSalesText.has(configuration.productId)) {
        const packageDetails = getPackageDetails(configuration.packagePath);
        dispatch(
          getSalesText({
            productId: configuration.productId,
            packageVersion: configuration.packagePath.split('~')[1],
            packageCode: packageDetails?.packageCode,
            packageType: packageDetails?.packageType,
            subModels: configuration.subModels,
          })
        );
      }

      //get diff package details
      const config = configuration.configurations.find(
        (config) => config.configurationId === configuration.configurationId
      );
      if ((!config?.isValid || !config?.isComplete) && !diffResponse) {
        getDiffPackageDetails();
      }
    }
  }, [configuration?.data]);

  //added the diff response rows to the current state
  useEffect(() => {
    if (diffResponse?.added?.length > 0) {
      dispatch(updateDiffFeature(diffResponse.added));
    }
  }, [diffResponse]);

  async function getConfigurationDetails() {
    const configurationId = configState?.state?.configId ?? params.configurationId;
    const currentConfiguration: IConfigurationDetail = configuration.configurations.find(
      (config: IConfigurationDetail) => config.configurationId === configurationId
    );

    let res;
    if (!currentConfiguration) {
      res = await searchConfiguration(configurationId);
      if (res?.data) {
        dispatch(addConfiguration(res?.data));
      }
    }

    const configRequest = currentConfiguration
      ? currentConfiguration.configureRequest
      : { ...res.data.userAssignments, configurationId: configurationId };

    const configureRequest = createConfigureRequest(
      configRequest,
      EView.CPQ,
      currentConfiguration ? currentConfiguration.productId : res.data.productInfo.id
    );

    dispatch(getConfigurationData(configureRequest));
  }

  if (!user.details) {
    return <Unauthorized />;
  }

  return (
    <div className="details-page">
      <Box className="d-flex w-100">
        <TabDrawer />
        {configuration?.data && (
          <DisplayTableView pmDetails={configuration.data} diffResponse={diffResponse} />
        )}
      </Box>
    </div>
  );
}

/**
 * This function returns the icons based the diffrent action type for feature and options
 * @param {boolean} isFound
 * @param {string} changeType
 * @param {ITranslateProps} t
 * @returns {React.JSX.Element}
 */
function GetTitleTooltip(
  isFound: boolean,
  changeType: string,
  row: IDetailPageRow,
  handleOpenDiffDialog,
  t: ITranslateProps
): React.JSX.Element {
  if (!isFound) {
    return <></>;
  }

  const action = changeType.toLocaleLowerCase();
  switch (action) {
    case EDiffAction.Modified:
      return (
        <Tooltip
          title={t('tooltip.modified')}
          placement="top"
          slotProps={{ popper: { disablePortal: true } }}
        >
          <Box display="inline-flex" alignItems="center">
            <BorderColorIcon
              onClick={() => handleOpenDiffDialog(row)}
              sx={{ color: 'grey', cursor: 'pointer' }}
            />
          </Box>
        </Tooltip>
      );

    case EDiffAction.Deleted:
      return (
        <Tooltip
          title={t('tooltip.deleted')}
          placement="top"
          slotProps={{ popper: { disablePortal: true } }}
        >
          <Box display="inline-flex" alignItems="center">
            <RemoveCircleOutlineIcon
              onClick={() => handleOpenDiffDialog(row)}
              sx={{ color: 'red', cursor: 'pointer' }}
            />
          </Box>
        </Tooltip>
      );
    default:
      return <></>;
  }
}

/**
 * This function check whether show the icon in the data table if product is inValid
 * @param {GridRenderCellParams} params input params
 * @param {IDiffResponse} diffResponse diff api response
 * @param {ITranslateProps} t trnslation key
 * @returns {React.JSX.Element} return the icons
 */
function showOptionIcons(
  params: GridRenderCellParams,
  diffResponse: IDiffResponse,
  handleOpenDiffDialog,
  t: ITranslateProps
): React.JSX.Element {
  if (params.row.added) {
    return (
      <Tooltip
        title={t('tooltip.added')}
        placement="top"
        slotProps={{ popper: { disablePortal: true } }}
      >
        <Box display="inline-flex" alignItems="center">
          <AddCircleOutlineIcon onClick={handleOpenDiffDialog} sx={{ color: 'green' }} />
        </Box>
      </Tooltip>
    );
  }

  //check for modified
  const checks = [checkIsOptionAdded, checkIsOptionRemovedFromUpdated];
  let changeType = '';
  const isModified =
    diffResponse?.updated?.some((item) =>
      checks.some((check) => {
        const result = check(item, params);
        if (result) {
          changeType = result;
          return true;
        }
        return false;
      })
    ) || false;

  // Check for removals
  const isRemoved =
    diffResponse?.removed?.some((removedItem) => {
      const result = checkIsOptionRemoved(removedItem, params);
      if (result && !changeType) {
        changeType = result;
        return true;
      }
      return false;
    }) || false;

  return GetTitleTooltip(isModified || isRemoved, changeType, params.row, handleOpenDiffDialog, t);
}

/**
 * This function check whether show the icon in the data table if product is inValid
 * @param {GridRenderCellParams} params input params
 * @param {IDiffResponse} diffResponse diff api response
 * @param {ITranslateProps} t trnslation key
 * @returns {React.JSX.Element} return the icons
 */
function showFeatureIcons(
  params: GridRenderCellParams,
  diffResponse: IDiffResponse,
  handleOpenDiffDialog,
  t: ITranslateProps
): React.JSX.Element {
  if (params.row.added) {
    return (
      <Tooltip
        title={t('tooltip.added')}
        placement="top"
        slotProps={{
          popper: { disablePortal: true },
        }}
      >
        <Box display="inline-flex" alignItems="center">
          <AddCircleOutlineIcon onClick={handleOpenDiffDialog} sx={{ color: 'green' }} />
        </Box>
      </Tooltip>
    );
  }

  //check for modified
  const checks = [checkIsPropertiesModified, checkIsFeatureModified];
  let changeType = '';
  const isModified =
    diffResponse?.updated?.some((item) =>
      checks.some((check) => {
        const result = check(item, params);
        if (result) {
          changeType = result;
          return true;
        }
        return false;
      })
    ) || false;

  // Check for removals
  const isRemoved =
    diffResponse?.removed?.some((removedItem) => {
      const result = checkIsFeatureRemoved(removedItem, params);
      if (result && !changeType) {
        changeType = result;
        return true;
      }
      return false;
    }) || false;

  return GetTitleTooltip(isModified || isRemoved, changeType, params.row, handleOpenDiffDialog, t);
}
